import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';

import Badge from '~components/Badge';
import Tooltip from '~components/common/Tooltip';
import ExclamationMark from '~components/Icon/ExclamationMark';
import LoadingCircle from '~components/Loading';
import { COMPANY_DETAIL_MENU } from '~constant/company';
import CompanySectionTitle from '~features/company/CompanySectionTitle';
import EmployeeChart from '~features/company/EmployeeInfo/EmployeeChart';
import { useGetCompanyEmplyee } from '~hooks/query/company/useCompanyEmployee';
import { useRouterQuery } from '~hooks/useRouterQuery';
import { ValueOf } from '~types/common';
import {
  COMPANY_SOURCE,
  CompanyEmployee,
  COMPANYS_SOURCE_KEY,
  COMPANYS_SOURCE_VALUE,
  CompanyStates,
} from '~types/company';
import { getKeyByValue } from '~utils/common/object';
import { setGtmDataLayer } from '~utils/common/thirdPartyEvent';
import { isClient } from '~utils/lodash/isServer';

import BlockedInfo from '../BlockedInfo';
import Card from '../Card';
import DropDownSource from '../DropDownSource';
import ErrorSection from '../ErrorSection';
import EmployeeBarChart from './EmployeeBarChart';
import EmployeeRate from './EmployeeRate';
import EmployeeSummary from './EmployeeSummary';
import * as S from './style';

const emptyEmployee = {
  employee: null,
  industryAvgRate: null,
  industryEmployee: null,
  industryRate: null,
  rate: null,
  rateType: null,
  totalAvgRate: null,
  totalEmployee: null,
  totalRate: null,
};

const EMPTY_COMPANY_EMPLOYEE: CompanyEmployee = {
  hired: emptyEmployee,
  left: emptyEmployee,
  total: emptyEmployee,
  updatedAt: '',
};

function EmployeeInfo({
  registerRef,
  companyStates,
}: {
  registerRef: (element: HTMLElement, id: ValueOf<typeof COMPANY_DETAIL_MENU>) => void;
  companyStates: CompanyStates;
}) {
  const companyId = useRouterQuery('companyId');
  const theme = useTheme();
  const { data, defaultSource, status, source } = useGetCompanyEmplyee(
    companyStates.closed,
    companyId,
  );
  const [selectedSource, setSelectedSource] = useState<COMPANYS_SOURCE_VALUE>();

  const employee = useMemo<CompanyEmployee>(() => {
    const sourceKey = selectedSource
      ? (getKeyByValue(COMPANY_SOURCE, selectedSource) as COMPANYS_SOURCE_KEY)
      : defaultSource;

    if (!data?.employees || !sourceKey) {
      return EMPTY_COMPANY_EMPLOYEE;
    }

    return data?.employees[sourceKey];
  }, [data, selectedSource]);

  const onChange = (value: string) => {
    setGtmDataLayer('company__salarySource__click', {
      gnbKind: '인원 수',
      from: selectedSource || defaultSource,
      to: value,
    });

    setSelectedSource(value as COMPANYS_SOURCE_VALUE);
  };

  useEffect(() => {
    if (isClient) {
      if (defaultSource) setSelectedSource(COMPANY_SOURCE[defaultSource]);
    }
  }, [defaultSource]);

  const cardProps = {
    registerRef,
    title: '인원',
    sectionId: COMPANY_DETAIL_MENU.EMPLOYEE,
  };

  if (companyStates?.closed || companyStates?.deleted) {
    return null;
  }

  if (status === 'error') {
    return (
      <Card {...cardProps}>
        <ErrorSection />
      </Card>
    );
  }

  if (!companyStates?.employee) {
    return (
      <Card {...cardProps}>
        <BlockedInfo />
      </Card>
    );
  }

  if (status === 'loading' || !employee) {
    return (
      <Card {...cardProps}>
        <LoadingCircle />
      </Card>
    );
  }

  return (
    <Card
      {...cardProps}
      rightCardHeader={
        !(companyStates?.deleted || companyStates?.closed) && (
          <DropDownSource
            value={selectedSource || defaultSource}
            options={source}
            emptyValue={!(selectedSource || defaultSource) ? '정보없음' : undefined}
            tooltipHelperText={
              employee.updatedAt
                ? `${dayjs(employee.updatedAt).format('YYYY.MM')} 기준 :`
                : '인원출처 :'
            }
            tooltipText={
              '출처의 데이터를 기반으로 추정된 값으로, 참고 자료로만 활용하시길 바랍니다.'
            }
            onChange={onChange}
          />
        )
      }>
      <EmployeeSummary employee={employee.total} />
      <S.EmployeeRateWrapper>
        <EmployeeRate title={'총 인원'} employee={employee.total} type='total' />
        <EmployeeRate
          title={'최근 1년 퇴사율'}
          tooltipText={'퇴사율 = 최근 1년 국민연금 상실자 총합/</br>최근 1년 월 평균 재직인원'}
          employee={employee.left}
          type='left'
        />
        <EmployeeRate
          title={'최근 1년 인원증가율'}
          tooltipText={'인원증가율 = 최근 1년 국민연금 취득자 총합/</br>최근 1년 월 평균 재직인원'}
          employee={employee.hired}
          type='hired'
        />
      </S.EmployeeRateWrapper>
      <S.EmployeeChartWrapper>
        <CompanySectionTitle inlineCss={S.cssTitleChart}>
          <Badge bgColor={theme.colors.magenta700} fontColor={theme.colors.magenta100}>
            NEW
          </Badge>
          평균 근속연수 (금감원)
          <Tooltip
            width={280}
            tooltipText={`업계 평균: 크레딧잡 내 근속 연수 데이터가 있는 동일 업종 기업의 평균입니다. 
              <br/>전체 평균: 크레딧잡 내 근속 연수 데이터가 있는 전체 기업의 평균입니다. 
              <br/><br/>
              *업종에 따라 업계 평균이 보이지 않을 수 있습니다.`}
            position='center'>
            <ExclamationMark width={15} color={theme.colors.gray700} />
          </Tooltip>
        </CompanySectionTitle>
        <S.ChartWrapper>
          <EmployeeBarChart />
        </S.ChartWrapper>
      </S.EmployeeChartWrapper>
      <S.EmployeeChartWrapper>
        <CompanySectionTitle inlineCss={S.cssTitleChart}>
          월별그래프 (국민연금)
          <Tooltip
            width={215}
            tooltipText={
              '국민연금 데이터를 기반으로 산출된<br/>그래프입니다. 선택하신 인원출처가<br/>다를 경우 결과가 상이할 수 있습니다.'
            }
            position='center'>
            <ExclamationMark width={15} color={theme.colors.gray700} />
          </Tooltip>
        </CompanySectionTitle>
        <S.ChartWrapper>
          <EmployeeChart companyId={companyId} />
        </S.ChartWrapper>
      </S.EmployeeChartWrapper>
    </Card>
  );
}

export default EmployeeInfo;
